import { useEffect, useCallback, useContext, useState, useRef } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { AI_TITLE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

interface UseEditorPasteDetectionProps {
	editorAPI?: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	contentId: string;
	cloudId: string;
	title: string | null;
	onTitleChange?: (title: string) => void;
	userOffline?: boolean;
}

interface UseEditorPasteDetectionReturn {
	shouldGenerateTitle: boolean;
	generateTitleFromPaste: () => void;
	isTitleGenerating: boolean;
	shouldShowStreaming: boolean;
	documentContext: string;
	onStreamingComplete: () => void;
	onStreamingError: () => void;
}

export const useEditorPasteDetection = ({
	editorAPI,
	contentId,
	title,
	userOffline,
}: UseEditorPasteDetectionProps): UseEditorPasteDetectionReturn => {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [isTitleGenerating, setIsTitleGenerating] = useState(false);
	const [shouldShowStreaming, setShouldShowStreaming] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const proseMirrorElementRef = useRef<HTMLElement | null>(null);
	const titleRef = useRef(title);

	const { isEditorEmpty } = useSharedPluginStateWithSelector(
		editorAPI || ({} as any),
		['core'],
		({ coreState }) => ({
			isEditorEmpty: !!coreState?.isEmpty,
		}),
	) as { isEditorEmpty: boolean };

	useEffect(() => {
		titleRef.current = title;
	}, [title]);

	const isFeatureEnabled =
		isAIEnabledForContent &&
		!userOffline &&
		expValEquals('confluence_ai_title_generation_on_paste', 'isEnabled', true);

	const [documentContext, setDocumentContext] = useState<string>('');

	const findProseMirrorElement = useCallback((): HTMLElement | null => {
		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		return filteredProseMirrors[0] as HTMLElement | null;
	}, []);

	const generateTitleFromPaste = useCallback(() => {
		const currentTitle = titleRef.current;

		if (!isFeatureEnabled || (currentTitle && currentTitle.trim() !== '')) {
			return;
		}

		const proseMirrorElement = proseMirrorElementRef.current || findProseMirrorElement();

		if (!proseMirrorElement) {
			return;
		}

		const extractedContext = proseMirrorElement.textContent || '';

		if (extractedContext.trim().length === 0) {
			return;
		}

		setIsTitleGenerating(true);
		experienceTracker?.start({
			name: AI_TITLE_GENERATION,
		});

		setDocumentContext(extractedContext);
		setShouldShowStreaming(true);
	}, [isFeatureEnabled, experienceTracker, findProseMirrorElement]);

	useEffect(() => {
		// Only check if feature is enabled, not if title is empty
		// This keeps the listener active even after a title is generated
		if (!isFeatureEnabled) {
			return;
		}

		const proseMirrorElement = findProseMirrorElement();
		proseMirrorElementRef.current = proseMirrorElement;

		if (!proseMirrorElement) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const pastedText = event.clipboardData?.getData('text/plain') || '';

			if (pastedText.length > 50) {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				timeoutRef.current = setTimeout(() => {
					generateTitleFromPaste();
					timeoutRef.current = null;
				}, 100);
			}
		};

		proseMirrorElement.addEventListener('paste', handlePaste);

		return () => {
			proseMirrorElement.removeEventListener('paste', handlePaste);

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [findProseMirrorElement, isFeatureEnabled, generateTitleFromPaste]);

	const onStreamingComplete = useCallback(() => {
		setIsTitleGenerating(false);
		setShouldShowStreaming(false);
	}, []);

	const onStreamingError = useCallback(() => {
		setIsTitleGenerating(false);
		setShouldShowStreaming(false);
	}, []);

	return {
		shouldGenerateTitle: isFeatureEnabled && (!title || title.trim() === '') && !isEditorEmpty,
		generateTitleFromPaste,
		isTitleGenerating,
		shouldShowStreaming,
		documentContext,
		onStreamingComplete,
		onStreamingError,
	};
};
